/* eslint-disable max-len */
import _ from 'underscore';
import Promise from 'bluebird';
import ko from 'knockout';
import ReactDOM from 'react-dom';
import React from 'react';
import { InputControl } from '@bingads-webui/input-control';
import { RemainingCharsHint } from '@bingads-webui/remaining-chars-hint';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import {
  AudienceCampaignErrorsView,
  getBidSchema,
  getBulkErrorsLogMessage,
  getCampaignType,
  CampaignSubTypeTextMapping,
  AudienceStepBase,
  getBoostSetting,
  BiddingStrategyToRangeKey,
  PricingModelToBidParam,
  BiddingStrategyToPricingModel,
  BiddingStrategyValueToRecommendBidKey,
  generateNameSuggestionSuffix,
} from '@bingads-webui-campaign/audience-campaign-shared';
import { CashbackSettings } from '@bingads-webui-campaign/cashback-settings';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { FrequencyCapping } from '@bingads-webui-campaign/frequency-capping';
import { BoostPlacementTarget } from '@bingads-webui-campaign-react/boost-placement-target';
import { ContextualTargetWithStyle, getSelectionFromCriterions, getContextualTargetInfo } from '@bingads-webui-campaign-react/boost-contextual-target';
import { shouldSupportCashbackSettings, logAdGroupPersonalizedOffers } from '@bingads-webui-campaign/personalized-offer-shared';
import { AudienceAdvancedSettings } from '../advanced-settings/view';

import template from './template.pug';
/**
 * CrudAudienceAdgroupBasic class that extends {AudienceStepBase} class
 * The class defines the section of the setting budget and bid
 *
 * @class CrudAudienceAdgroupBasic
 * @extends {AudienceStepBase}
 */
export class CrudAudienceAdgroupBasic extends AudienceStepBase {
  /**
  * Initializes the {CrudAudienceAdgroupBasic} class.
  *
  * @param {Object} options - options for initializing the class.
  * @param {Object} options.viewContext = viewContext for the View.
  * @returns {undefined}
  */
  initialize(options) {
    this.viewContext = options.viewContext;
    this.wizardElement = options.wizardElement;
    this.currentLevelTargetInfo = options.currentLevelTargetInfo;
    this.scenario = options.scenario;
    this.currentCampaign = options.viewContext.currentCampaign;
    this.currentAdGroup = options.viewContext.currentAdGroup;
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.decimal = DependencyResolver.get('component/humanize/decimal');
    this.i18nErrors = DependencyResolver.get('component/api-errors-campaign/index');
    this.deps = options.deps || {};
    this.isNewUI = _.result(this.deps, 'isNewUI', false);
    this.isNextButtonEnabled = (
      !(this.viewContext.isReadOnlyUser || this.viewContext.CurrentAccountInActive)
    );
    const config = DependencyResolver.get('component/config/index');
    const permissions = config.get('Permissions');
    const {
      IsAudienceCampaignCollapseTargetSelectionEnabled,
      IsAmplifyEnabled,
      IsContextualTargetEnabled,
    } = permissions;

    const bidStrategyType = this.currentCampaign && this.currentCampaign.BiddingScheme && this.currentCampaign.BiddingScheme.Type;
    const autoBidStrategyList = ['MaxClicks', 'MaxConversions', 'TargetCpa', 'TargetRoas', 'MaxConversionValue', 'TargetImpressionShare', 'CostPerSale'];
    this.isAutoBidding = _.contains(autoBidStrategyList, bidStrategyType);

    this.workflow = options.workflow;
    this.boostPlacementReactComponent = React.createRef();

    this.isAudienceCampaignCollapseTargetSelectionEnabled = IsAudienceCampaignCollapseTargetSelectionEnabled;
    this.isMSANBoostEnabled = IsAmplifyEnabled;
    this.isContextualTargetEnabled = this.isMSANBoostEnabled && IsContextualTargetEnabled;
    this.isFCapEnabled = bidStrategyType === 'ManualCpm';

    this.supportCashbackSettings = shouldSupportCashbackSettings(this.currentCampaign, permissions);

    if (this.supportCashbackSettings) {
      this.initCashbackSettings(this.currentAdGroup, this.currentCampaign);

      if (this.cashbackSettingsView) {
        this.cashbackSettingsView.viewModel.CashbackPercent
          .subscribe((v) => {
            if (this.cashbackSettingsView.viewModel.validateCashbackPercent(v)) {
              this.onCashbackPercentChange(v);
            }
          });
      }
    }

    this.initializeNameInputControl();

    this.initializeBiddingStrategyView();
    this.initDefaultBidInput();

    this.initAudienceAdvancedSettings();

    this.errorsView = new AudienceCampaignErrorsView();

    this.logScenario = options.logScenario;

    if (this.isContextualTargetEnabled) {
      this.contextualSelection = [];

      const contextualTargetsCriterions = this.currentLevelTargetInfo.getAllByType('ContextualTarget');
      this.contextualSelection = getSelectionFromCriterions(contextualTargetsCriterions);
      // reference copy is OK enough as the selection change comes as a new array.
      this.initialContextualSelection = this.contextualSelection;
    }

    this.frequencyCapping = [{}];

    if (this.currentAdGroup && this.currentAdGroup.FrequencyCapSettings) {
      this.frequencyCapping = this.currentAdGroup.FrequencyCapSettings;
    }

    this.initialFrequencyCapping = _.clone(this.frequencyCapping);

    this.viewmodel = {
      isAdvancedAdgroupExpanded: ko.observable(false),
      isAutoBidding: this.isAutoBidding,
    };
  }

  /**
  * Define events and event handlers for them.
  *
  * @readonly
  * @returns {Object} returns object defining events
  */
  get events() {
    return {
      'click .audience-advanced-settings-header': 'toggleAdvancedSettings',
    };
  }

  /**
   * Get the actions of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns array that has information about the actions.
   */
  get actions() {
    const actions = [
      {
        name: 'next',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Next'),
        enabled: this.isNextButtonEnabled,
      },
    ];

    if (this.viewContext.currentAdGroup) {
      actions.push({
        name: 'done',
        enabled: this.isNextButtonEnabled,
        htmlAttributes: {
          class: 'btn btn-primary btn-exitWithSave',
        },
        method: 'saveAndExit',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Exit'),
      });
    } else {
      actions.push({
        name: 'cancel',
        displayText: this.i18n.getString('Audience_Campaign_Cancel_Label'),
      });
    }

    return actions;
  }

  onCashbackPercentChange(value) {
    const formattedValue = value === '' || value == null ? null : this.decimal.fromLocalToFloat(value);
    if (this.advancedSettings &&
      this.advancedSettings.adScheduleView) {
      this.advancedSettings.adScheduleView
        .toggleCashbackPercentColumns(true, {
          level: 'AdGroup',
          value: formattedValue,
        });
    }
  }

  /**
   * Saves the AdGroup and exits.
   * This is only used when 'Save & Exit' button is shown on this step.
   *
   * @returns {undefined} - returns undefined
   */
  saveAndExit() {
    return this.save().then((isSuccess) => {
      if (isSuccess) {
        this.trigger('saveAndExit');
      }
    });
  }

  /**
  * Toggle Advanced Settings Section
  *
  * @private
  * @returns {undefined}
  */
  toggleAdvancedSettings() {
    this.$('#advancedSettings').toggle();
    this.$('.audience-advanced-settings-header .bedded-icon-marker').toggleClass('expanderImage expandedImage');
    this.viewmodel.isAdvancedAdgroupExpanded(!this.viewmodel.isAdvancedAdgroupExpanded());
  }

  /**
   * Initialize Name input control
   *
   * @private
   * @returns {undefined}
   */
  initializeNameInputControl() {
    const schema = DependencyResolver.get('$/schema!campaign/AdGroup');
    const SchemaModel = backboneSchema(
      schema,
      this.i18n,
      ['Name']
    );

    this.adGroupNameInputModel = new SchemaModel();

    if (this.currentAdGroup) {
      this.adGroupNameInputModel.set('Name', this.currentAdGroup.Name);
    }

    this.nameInput = new InputControl({
      entity: this.adGroupNameInputModel,
      field: 'Name',
      ariaLabelledby: 'adgroupNameLabel',
    });

    if (this.isAudienceCampaignCollapseTargetSelectionEnabled && _.isEmpty(this.adGroupNameInputModel.get('Name'))) {
      this.nameInput.setState({ value: generateNameSuggestionSuffix('Adgroup') });
    }

    this.nameInputRemainingCharsHint = new RemainingCharsHint({
      i18n: this.i18n,
      model: this.adGroupNameInputModel,
      field: 'Name',
    });
  }

  /**
   * Initialize Bidding Strtegy view
   *
   * @private
   * @returns {undefined}
   */
  initializeBiddingStrategyView() {
    const BiddingStrategyView = DependencyResolver.get('campaignui/feature/bidding-strategy/view/setting');
    const campaignType = getCampaignType(this.viewContext.currentCampaign);
    const campaignSubType = CampaignSubTypeTextMapping[this.viewContext.currentCampaign && this.viewContext.currentCampaign.CampaignSubType];
    // in adgroup setting page
    if (this.currentAdGroup && this.currentAdGroup.BiddingScheme) {
      this.biddingStrategyView = new BiddingStrategyView({
        entityType: 'AdGroup',
        biddingStrategyType: this.currentAdGroup.BiddingScheme.Type,
        campaignSubType,
        campaignType,
      });
    } else {
      // in create adgroup
      let biddingStrategyType;
      if (
        this.viewContext.currentCampaign &&
        campaignSubType === 'AudienceVideoAds' &&
        this.viewContext.currentCampaign.BiddingScheme &&
        this.viewContext.currentCampaign.BiddingScheme.Type
      ) {
        biddingStrategyType = this.viewContext.currentCampaign.BiddingScheme.Type;
      }
      this.biddingStrategyView = new BiddingStrategyView({
        entityType: 'AdGroup',
        isInitialize: true,
        biddingStrategyType,
        campaignSubType,
        campaignType,
      });
    }
  }

  /**
   * Initialize Default Bid input
   *
   * @private
   * @returns {undefined}
   */
  initDefaultBidInput() {
    const config = DependencyResolver.get('component/config/index');
    const bidSchemeType = this.viewContext.currentCampaign &&
      this.viewContext.currentCampaign.BiddingScheme &&
      this.viewContext.currentCampaign.BiddingScheme.Type;

    const DefaultBidModel = backboneSchema(getBidSchema({
      bidCeil: _.result(config.get(BiddingStrategyToRangeKey[bidSchemeType] || 'KeywordBidRange'), 'CeilingValue'),
      bidFloor: _.result(config.get(BiddingStrategyToRangeKey[bidSchemeType] || 'KeywordBidRange'), 'FloorValue'),
    }), this.i18n, ['Amount']);

    this.defaultBidModel = new DefaultBidModel();

    if (_.get(this.viewContext, 'currentCampaign.CampaignType') === 'Audience'
    && _.contains([0, 'AudienceStatic'], _.get(this.viewContext, 'currentCampaign.CampaignSubType'))
    && _.contains(['ManualCpc', 'EnhancedCpc'], this.currentCampaign.BiddingScheme.Type)) {
      const recommendedValueFromContext = config.get(BiddingStrategyValueToRecommendBidKey[bidSchemeType]);
      const recommendedValue = _.isNull(recommendedValueFromContext) || _.isUndefined(recommendedValueFromContext) ? 3.3 : recommendedValueFromContext;
      this.defaultBidModel.set(
        'Amount',
        this.i18n.formatDecimal(recommendedValue, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      );
    }

    if (this.currentAdGroup) {
      const bidParam = PricingModelToBidParam[this.currentAdGroup.PricingModel] || 'SearchBid';
      const amount = this.currentAdGroup[bidParam];
      if (amount) {
        this.defaultBidModel.set('Amount', amount.toString());
      }
    }

    const AutoBidModel = backboneSchema({
      name: 'AutoBid',
      type: 'object',
      properties: {
        Amount: {
          type: 'string',
        },
      },
    }, this.i18n, ['Amount']);
    this.autoBidModel = new AutoBidModel();
    this.autoBidModel.set('Amount', 'Auto');

    this.defaultBidInput = new InputControl({
      entity: this.isAutoBidding ? this.autoBidModel : this.defaultBidModel,
      field: 'Amount',
      addonText: this.viewContext.accountCurrencyCode,
      ariaLabelledby: 'defaultBidLabel',
      readonly: this.isAutoBidding,
    });
  }

  /**
   * Initialize Advanced Settings view
   *
   * @private
   * @returns {undefined}
   */
  initAudienceAdvancedSettings() {
    this.advancedSettings = new AudienceAdvancedSettings({
      viewContext: this.viewContext,
      scenario: this.scenario,
      isNewUI: this.isNewUI,
      supportCashbackSettings: this.supportCashbackSettings,
    });
  }

  initCashbackSettings(adGroup, campaign) {
    const CashbackPercent = _.chain(adGroup)
      .result('AdGroupCashback')
      .result('Percent', null)
      .value();
    const CashbackScope = _.chain(adGroup)
      .result('AdGroupCashback')
      .result('Scope')
      .value() || 'None';
    const CouponsEnabled = _.chain(adGroup)
      .result('AdGroupCashback')
      .result('CouponsEnabled', 'None')
      .value();
    const ParentLevelCashbackPercent = _.chain(campaign)
      .result('CampaignCashback')
      .result('Percent', null)
      .value();
    const ParentLevelCashbackScope = _.chain(campaign)
      .result('CampaignCashback')
      .result('Scope')
      .value() || 'None';
    const ParentCouponsEnabled = _.chain(campaign)
      .result('CampaignCashback')
      .result('CouponsEnabled', 'None')
      .value();
    if (this.supportCashbackSettings) {
      this.cashbackSettingsView = new CashbackSettings({
        CashbackPercent,
        CashbackScope,
        showEnableCashbackSettingsCheckbox: false,
        showCashbackBudget: false,
        isCashbackScopeReadonly: false,
        showInheritFromParentOption: true,
        ParentLevelCashbackPercent,
        ParentLevelCashbackScope,
        CouponsEnabled,
        ParentCouponsEnabled,
        deps: {
          ...this.deps,
          i18n: this.i18n,
        },
      });
    }
  }

  /**
   * Get the title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get title() {
    return {
      text: this.i18n.getString('Audience_Campaign_Crud_Adgroup_Basic_Title'),
    };
  }

  /**
   * Get the page title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get pageTitle() {
    return {
      text: this.i18n.getString('Audience_Campaign_Crud_Adgroup_Basic_PageTitle'),
    };
  }

  onContextualSelectionChange(data) {
    this.contextualSelection = data || [];
  }

  onFrequencyCappingChange(data) {
    this.frequencyCapping = data.FrequencyCapSettings;
    this.renderFrequencyCapping();
  }

  renderFrequencyCapping() {
    if (this.isFCapEnabled) {
      ReactDOM.render(
        <FrequencyCapping
          i18n={this.deps.i18nNewDesign}
          legacyI18n={this.i18n}
          validationProps={{
            FrequencyCapSettings: this.frequencyCapping,
          }}
          updateValidation={(message, data) => this.onFrequencyCappingChange(data)}
        />,
        this.$('.frequency-capping-container')[0]
      );
    }
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    this.$el.html(template(this));

    ko.applyBindings(this.viewmodel, this.el);

    this.renderFrequencyCapping();

    if (this.isMSANBoostEnabled && this.scenario !== 'adgroup-creation') {
      ReactDOM.render(
        <BoostPlacementTarget
          boostSetting={getBoostSetting(this.currentAdGroup)}
          ref={this.boostPlacementReactComponent}
          i18n={this.deps.i18nNewDesign}
        />,
        this.$('.boost-placement-target')[0]
      );
    }

    if (this.isContextualTargetEnabled && this.scenario !== 'adgroup-creation') {
      ReactDOM.render(
        <ContextualTargetWithStyle
          i18n={this.deps.i18nNewDesign}
          contextualSelection={this.contextualSelection}
          onContextualSelectionChange={selection => this.onContextualSelectionChange(selection)}
        />,
        this.$('.contextual-target-container')[0]
      );
    }

    this.$('.name-input-control-container').prepend(this.nameInput.render().$el);
    this.$('.remaining-chars-hint-container').append(this.nameInputRemainingCharsHint.render().$el);

    this.$('.bidding-strategy-container').append(this.biddingStrategyView.render().$el);

    this.$('.default-bid-container').append(this.defaultBidInput.render().$el);

    if (this.cashbackSettingsView) {
      this.$('.cashback-settings-container').append(this.cashbackSettingsView.render().$el);
    }

    this.$('.audience-advanced-settings-container').append(this.advancedSettings.render().$el);

    this.$('.errors-container').append(this.errorsView.render().$el);

    return this;
  }

  /**
   * Get the object data to save the AdGroup.
   *
   * @returns {Object} - data representation of the object.
   */
  getData() {
    let pricingModel = 'CPC';
    if (
      this.viewContext.currentCampaign.BiddingScheme &&
      this.viewContext.currentCampaign.BiddingScheme.Type
    ) {
      pricingModel =
      BiddingStrategyToPricingModel[this.viewContext.currentCampaign.BiddingScheme.Type] ||
        pricingModel;
    }
    let params = {
      Name: this.adGroupNameInputModel.toJSON().Name,
      Medium: 'Search',
      PricingModel: pricingModel,
      Languages: ['All'],
      Campaign: {
        Id: this.viewContext.currentCampaign.Id,
      },
      BiddingScheme: this.biddingStrategyView.getValues(),
      [PricingModelToBidParam[pricingModel]]: this.defaultBidModel.toJSON().Amount,
    };

    if (this.viewContext.currentAdGroup) {
      params.Id = this.viewContext.currentAdGroup.Id;
    }

    if (this.isMSANBoostEnabled && this.scenario !== 'adgroup-creation') {
      params = {
        ...params,
        ...this.boostPlacementReactComponent.current.getData(),
      };
    }

    _.extend(params, this.advancedSettings.getData());

    const scheduling = this.getScheduleInfo();

    params.AdGroupCriterions = this.populateCriterionWithAdGroupInfo(scheduling);

    if (this.isContextualTargetEnabled) {
      const contextualTargets = getContextualTargetInfo(
        this.initialContextualSelection,
        this.contextualSelection
      );
      this.populateCriterionWithAdGroupInfo(contextualTargets);

      params.AdGroupCriterions = [
        ...(_.isArray(params.AdGroupCriterions) ? params.AdGroupCriterions : []),
        ...contextualTargets,
      ];
    }

    if (this.cashbackSettingsView) {
      _.extend(params, this.cashbackSettingsView.getValues());
    }

    if (this.isFCapEnabled) {
      if (JSON.stringify(this.initialFrequencyCapping) !== JSON.stringify(this.frequencyCapping)) {
        _.extend(params, { FrequencyCapSettings: [{}] });

        if (this.frequencyCapping) {
          params.FrequencyCapSettings = this.frequencyCapping;
        }
      }
    }

    return params;
  }

  validate() {
    const isAdvancedSettingsValid = this.advancedSettings.validate();
    const isCashbackSettingsValid = this.cashbackSettingsView ? this.cashbackSettingsView.validate() : true;

    if (!this.nameInput.getState().isValid
      || !this.defaultBidInput.getState().isValid
      || !isAdvancedSettingsValid
      || !isCashbackSettingsValid) {
      this.nameInput.setState({ showError: true });
      this.defaultBidInput.setState({ showError: true });

      if (!isAdvancedSettingsValid) {
        if (!this.$('#advancedSettings').is(':visible')) {
          this.toggleAdvancedSettings();
        }
        this.$('#advancedSettings')[0].scrollIntoView();
      } else {
        this.nameInput.$el[0].scrollIntoView(false);
      }

      return false;
    }

    return true;
  }

  /**
   * It attempts to save and return true if the save is done succefully.
   *
   * @returns {boolean} - returns true if it is ready to go to the next step.
   */
  wizardShouldGoNext() {
    return this.save();
  }

  /**
   * Save the adgroup with the given setting.
   *
   * @returns {Promise} - returns promise of returning boolean value indicating
   * if the save process is succesful or not.
   */
  save() {
    if (!this.validate()) {
      return Promise.resolve(false);
    }

    const jsdata = DependencyResolver.get('component/js-data-campaign/index');
    const params = this.getData();

    let updateOrCreateTask = null;

    if (params.Id) {
      updateOrCreateTask = jsdata.AdGroup.update(
        params.Id,
        params,
        { adapter: 'bulk', cacheResponse: false }
      );
    } else {
      updateOrCreateTask = jsdata.AdGroup.create(params, { adapter: 'bulk', cacheResponse: false });
    }

    this.errorsView.removeErrors();

    const createAdGroupBasicActivity = this.logScenario.createAdGroupBasic.create();

    return updateOrCreateTask.then((data) => {
      if (_.isFinite(data.Id)) {
        this.viewContext.currentAdGroup = data;
        logAdGroupPersonalizedOffers(
          this.supportCashbackSettings,
          createAdGroupBasicActivity.signal.bind(createAdGroupBasicActivity), params
        );
        return this.advancedSettings.updateAdScheduleViewWithContextAsync().then(() => true);
      }

      return Promise.resolve(false);
    })
      .catch((response) => {
        if (response && response.entity) {
          const hasErrors = response && !_.isUndefined(response.errors);

          if (hasErrors) {
            if (response.errors.length) {
              const adGroupErrors = _.partition(
                response.errors,
                error => error.Type === 'AdGroup Error'
              )[0];

              this.errorsView.setErrors(this.i18nErrors.getBulkApiErrors(adGroupErrors));

              createAdGroupBasicActivity.trace(getBulkErrorsLogMessage(response.errors));
            }
          } else {
            // Unexpected case. Log Error.
            createAdGroupBasicActivity.error(getBulkErrorsLogMessage(response.errors));
          }
        } else {
          // Ajax error
          this.errorsView.setErrors(this.i18nErrors.getODataErrors(response.errors));
        }

        return false;
      })
      .finally(withUiBlock(this.wizardElement, this.i18n.getString('Loading')));
  }

  /**
   * Populate AdGroup Criterion
   *
   * @private
   * @param {object} criterions - criterions to populate
   * @returns {object} - filtered criterions with AdGroupId
   */
  /* eslint-disable no-param-reassign */
  populateCriterionWithAdGroupInfo(criterions) {
    if (this.scenario === 'adgroup-settings') {
      _.each(criterions, (criterion) => {
        criterion.AdGroup = {
          Id: this.viewContext.currentAdGroup.Id,
        };
      }, this);
    } else {
      _.each(criterions, (criterion) => {
        criterion.CampaignName = this.viewContext.currentCampaign.Name;
        criterion.AdGroupName = this.adGroupNameInputModel.toJSON().Name;
      }, this);
    }

    return criterions;
  }

  /**
   * Get Ad Schedule info
   *
   * @private
   * @returns {object} scheduling info
   */
  getScheduleInfo() {
    const deletedDayTimeCb = (deletedDayTimeCriterions) => {
      _.each(deletedDayTimeCriterions, (criterion) => {
        criterion.Status = 'Deleted';
        if (this.scenario === 'adgroup-settings') {
          criterion.AdGroup = {
            Id: this.viewContext.currentAdGroup.Id,
          };
        } else {
          criterion.CampaignName = this.viewContext.currentCampaign.Name;
          criterion.AdGroupName = this.adGroupNameInputModel.toJSON().Name;
        }
      });
    };
    const scheduling = this.advancedSettings.adScheduleView.getValues(deletedDayTimeCb);

    return scheduling;
  }
}
